import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { CalendarPreference } from '@demind-inc/core';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import {
  useAuthContext,
  useCalendarContext,
  useUpdateCalendarPref,
} from '../../../../../../data-access';
import { CalendarAccountDropdown } from '../../../../../CalendarView';
import './DefaultCalendar.scss';
import { ReactComponent as DotCalendarIcon } from '../../../../../../assets/tabs/settings-tab/calendar-dot-icon.svg';

const DefaultCalendar: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { updateCalendarPref } = useUpdateCalendarPref();
  const { mainCalendar, calendarAccountsToCreateEvents, calendarPreferences } =
    useCalendarContext();

  const [defaultCalendarId, setDefaultCalendarId] = useState('');

  useEffect(() => {
    setDefaultCalendarId(calendarPreferences.defaultCalendarId || mainCalendar?.calendarId || '');
  }, [calendarPreferences.defaultCalendarId, mainCalendar?.calendarId]);

  const debouncedUpdateCalendarSettings = useMemo(
    () =>
      debounce((newData: Partial<CalendarPreference>) => {
        if (!user.preferenceId) {
          return;
        }

        return updateCalendarPref({
          preferenceId: user.preferenceId,
          preferences: { ...newData },
        });
      }, 1000),
    [updateCalendarPref, user.preferenceId]
  );

  useEffect(() => {
    return () => {
      debouncedUpdateCalendarSettings.cancel();
    };
  }, [debouncedUpdateCalendarSettings]);

  const handleDefaultCalendarChange = useCallback(
    (calendarId: string) => {
      setDefaultCalendarId(calendarId);

      debouncedUpdateCalendarSettings({
        defaultCalendarId: calendarId,
      });
    },
    [debouncedUpdateCalendarSettings]
  );

  return (
    <Stack
      id="defaultCalendar"
      gap={2}
      className="default-calendar"
      sx={{ scrollMarginTop: '12px' }}
    >
      <Stack gap={1} direction="row" alignItems="center">
        <DotCalendarIcon />
        <Typography variant="body1" className="default-calendar__section-title">
          {t('settings.calendar.defaultCalendar')}
        </Typography>
      </Stack>

      <Typography variant="subtitle2">
        {t('settings.calendar.defaultCalendarDescription')}
      </Typography>

      <CalendarAccountDropdown
        calendars={calendarAccountsToCreateEvents}
        onSelectCalendarId={handleDefaultCalendarChange}
        selectedCalendarId={defaultCalendarId}
        className="default-calendar__dropdown"
      />
    </Stack>
  );
};

export default DefaultCalendar;
